import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-brand-primary text-white shadow-sm hover:bg-brand-primary/90",
        primary:
          "bg-brand-primary text-white hover:bg-brand-primary/80 shadow-sm disabled:bg-brand-primary/75 disabled:text-white disabled:hover:bg-brand-primary/75",
        secondary:
          "text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 shadow-sm disabled:opacity-60",
        tertiary:
          "text-brand-gray border border-brand-gray/20 hover:bg-gray-200 disabled:opacity-60",
        warning:
          "text-white bg-cyan-500 hover:bg-cyan-600 shadow-sm disabled:bg-cyan-500/75 disabled:hover:bg-cyan-500/75",
        danger:
          "text-white bg-rose-500 hover:bg-rose-600 shadow-sm disabled:bg-rose-500/75 disabled:hover:bg-rose-500/75",
        destructive:
          "bg-rose-500 text-white shadow-sm hover:bg-rose-600 disabled:opacity-60",
        success:
          "text-white bg-teal-500 hover:bg-teal-600 shadow-sm disabled:bg-teal-500/75 disabled:hover:bg-teal-500/75",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-brand-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        pill: "w-full rounded-full px-3 py-2.5 text-xs font-semibold",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  styleVariant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "warning"
    | "danger"
    | "success";
  style?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "warning"
    | "danger"
    | "success"
    | React.CSSProperties;
  submit?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      styleVariant,
      style,
      submit,
      type,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const resolvedStyleVariant =
      typeof style === "string" ? style : styleVariant;
    const resolvedInlineStyle =
      typeof style === "object" ? style : undefined;
    const resolvedVariant = resolvedStyleVariant || variant || "default";
    const resolvedType = type || (submit ? "submit" : "button");
    return (
      <Comp
        type={asChild ? undefined : resolvedType}
        style={resolvedInlineStyle}
        className={cn(
          buttonVariants({
            variant: resolvedVariant as VariantProps<
              typeof buttonVariants
            >["variant"],
            size,
            className,
          }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export default Button;
export { Button, buttonVariants };
